import moment from 'moment/moment';
import { convertPhoneNumberStringToObject } from '@yuhuinc/ui-library';
import { applyCustomOrder } from 'helpers/StringHelper';
import currency from 'currency.js';
import uniqBy from 'lodash/uniqBy';
import { Triggers } from '../../payments/Fees/components/Form/constants';

export const buildInitialValuesForSelectedApplicant = (selectedApplicant) => {
  const birthday = selectedApplicant.birthday
    ? moment(selectedApplicant.birthday, 'YYYY-MM-DD')
    : null;
  const phoneNumber = selectedApplicant.phone_number
    ? convertPhoneNumberStringToObject(selectedApplicant.phone_number)
    : { code: '1', short: 'CA' };

  return { ...selectedApplicant, birthday, phone_number: phoneNumber };
};

export const isApplicationSubmitted = (application) => {
  return application?.lifecycle?.status === 'submitted';
};

export const isApplicationReviewRequired = (application) => {
  return ['review_required', 'details_added', 'change_consent_submitted'].includes(
    application?.lifecycle?.substatus
  );
};

export const isApplicationDetailsAdded = (application) => {
  return application?.lifecycle?.substatus === 'details_added';
};

export const isApplicationClosed = (application) => {
  return application.lifecycle?.status === 'closed';
};

export const isApplicantInvitable = (applicant) =>
  applicant?.lifecycle?.substatus === 'not_invited';

export const isApplicantRemindable = (applicant) =>
  ['not_started', 'in_progress', 'adding_details'].includes(applicant?.lifecycle?.substatus);

// Permission checking functions (used to guard the action buttons shown)

export const canApproveApplication = (application) =>
  isApplicationSubmitted(application) && !!application.can_approve;

export const canDenyApplication = (application) => canApproveApplication(application);

export const canCancelApplication = (application) =>
  !isApplicationClosed(application) && !!application.can_approve;

export const canReviewApplication = (application) =>
  (isApplicationReviewRequired(application) || isApplicationDetailsAdded(application)) &&
  !!application.can_review;

export const canCreateLeaseForApplication = (application) =>
  application.lifecycle?.substatus === 'lease_required';

export const canRequestAdditionalDetailsForApplication = (application) => {
  if (['unit_unavailable', 'details_required'].includes(application.lifecycle?.substatus)) {
    return false;
  }

  if (application.lifecycle?.substatus === 'decision_required' && !application.can_approve)
    return false;

  return (
    application.lifecycle?.status === 'submitted' &&
    (application.can_approve || application.can_review)
  );
};

export const canCurrentAgentViewApplication = (application) => {
  return !(application?.can_approve === false && application?.can_review === false);
};

export const canRequestChangesForApplication = (application) => {
  const allowedStatuses = ['submitted'];
  const allowedSubstatuses = [
    'review_required',
    'details_added',
    'decision_required',
    'change_consent_submitted',
  ];

  const userHasTheRightToRequestChanges = application.can_approve;

  return (
    allowedStatuses.includes(application?.lifecycle?.status) &&
    allowedSubstatuses.includes(application?.lifecycle?.substatus) &&
    userHasTheRightToRequestChanges
  );
};

export const canAssignNewUnitForApplication = (application) => {
  return (
    (application?.can_approve || application?.can_review) &&
    application.lifecycle?.status === 'submitted' &&
    application.lifecycle?.substatus === 'unit_unavailable'
  );
};

export const canRunBackgroundChecksForApplication = (application, applicant) => {
  const allowedApplicationStatuses = ['submitted', 'approved'];
  const allowedApplicationSubstatuses = [
    'review_required',
    'details_required',
    'details_added',
    'decision_required',
    'changes_required',
    'change_consent_required',
    'change_consent_submitted',
    'payment_required',
    'lease_required',
  ];

  return (
    allowedApplicationStatuses.includes(application?.lifecycle?.status) &&
    allowedApplicationSubstatuses.includes(application?.lifecycle?.substatus) &&
    (applicant ? applicant.lifecycle?.status === 'submitted' : true)
  );
};

export const shouldNotifyChangeApplicants = (application) => {
  return [
    'changes_required',
    'review_required',
    'details_added',
    'decision_required',
    'change_consent_submitted',
    'change_consent_required',
  ].includes(application?.lifecycle?.substatus);
};

export const shouldShowHoldForApplication = (application) => {
  return application?.lifecycle?.status === 'approved';
};

export const formatApplicantsData = (application) => {
  const applicants = application.cotenants.map((cotenant) => ({
    ...cotenant,
    custom_id: `Cotenant__${cotenant.id}`,
  }));

  const additionalOccupants = application.additional_occupants.map((additionalOccupant) => ({
    ...additionalOccupant,
    custom_id: `AdditionalOccupant__${additionalOccupant.id}`,
  }));

  const applicantList = [...applicants, ...additionalOccupants];

  const desiredOrder = ['lead_applicant', 'coapplicant', 'occupant', 'guarantor', 'reference'];
  return applyCustomOrder(applicantList, desiredOrder, 'applicant_type');
};

export const isThereOnlyOneApplicant = (application) => application?.applicants?.length === 1;

// TODO extract status/substatus sentinel values somewhere sensible.
export const atLeastOneApplicantSubmitted = (applicantList) =>
  applicantList.filter((applicant) => applicant?.lifecycle?.status === 'submitted').length > 0;

export const isOccupant = (applicant) => applicant.applicant_type === 'occupant';
export const notOccupant = (applicant) => !isOccupant(applicant);

export const getAdditionalApplicants = (application) => {
  const cotenants = application.cotenants.filter((cotenant) => !cotenant.is_primary_applicant);
  return cotenants.concat(application.additional_occupants);
};

export const getNonOccupantApplicants = (application) =>
  application?.applicants?.filter(notOccupant) || [];

export const generateBaseApplicationFormInitialValues = (application) => {
  let lease_items = (application?.lease?.lease_charges || []).map((leaseCharge) => ({
    id: leaseCharge.id,
    amount: currency(leaseCharge.amount.cents, { fromCents: true }),
    deposit_amount: currency(leaseCharge.deposit_amount.cents, { fromCents: true }),
    key_deposit_amount: currency(leaseCharge.key_deposit_amount.cents, { fromCents: true }),
    ancillary_item_id: leaseCharge.ancillary_item.id,
    asset_number: leaseCharge.ancillary_item.asset_number,
    item_type: leaseCharge.item_type,
    duration: leaseCharge.duration,
    start_date: leaseCharge.start_date ? moment(leaseCharge.start_date) : null,
    end_date: leaseCharge.end_date ? moment(leaseCharge.end_date) : null,
    ancillary_item_type_id: leaseCharge.ancillary_item.ancillary_item_type_id,
    assign_item_type_later: false,
  }));

  let desired_lease_items = (application?.lease?.desired_lease_items || []).map(
    (desiredLeaseItem) => ({
      ancillary_item_subtype_id: desiredLeaseItem.ancillary_item_subtype_id,
      asset_number: desiredLeaseItem.subtype_name,
      item_type: desiredLeaseItem.type_name,
      ancillary_item_type_id: desiredLeaseItem.ancillary_item_type_id,
      assign_item_type_later: true,
    })
  );

  lease_items.push(...desired_lease_items);

  return {
    is_draft: false,
    selectedApplicant:
      application.cotenants.find((cotenant) => cotenant.is_primary_applicant) || {},
    additional_applicants: getAdditionalApplicants(application) || [],
    selectedUnit: {
      ...(application.unit || {}),
      ancillary_items: (application.unit || {})?.ancillary_items?.map((ancillaryItem) => ({
        id: ancillaryItem.id,
        item_type: ancillaryItem.item_type,
        asset_number: ancillaryItem.asset_number,
        market_amount: currency(ancillaryItem.market_amount.cents, { fromCents: true }).value,
        market_deposit_amount: currency(ancillaryItem.market_deposit_amount.cents, {
          fromCents: true,
        }).value,
        market_key_deposit_amount: currency(ancillaryItem.market_key_deposit_amount.cents, {
          fromCents: true,
        }).value,
      })),
      property_id: application.property?.id,
    },
    application_package_id: application.application_package_id,
    deposit_required_for_lease: application.deposit_required_for_lease,
    lease_start_date: application.lease?.lease_start_date
      ? moment(application.lease?.lease_start_date)
      : null,
    lease_end_date: application.lease?.lease_end_date
      ? moment(application.lease?.lease_end_date)
      : null,
    lease_term: application.lease?.lease_term_in_months,
    early_move_in_date: application.lease?.early_move_in_date
      ? moment(application.lease?.early_move_in_date)
      : null,
    create_prorated_charge: application.lease?.create_prorated_charge,
    prorated_rent_value: application?.lease?.prorated_rent_value
      ? currency(application.lease.prorated_rent_value.cents, { fromCents: true })
      : null,
    monthly_rent: application?.lease?.lease_pricing?.rent
      ? currency(application.lease.lease_pricing.rent.cents, { fromCents: true })
      : null,
    deposit_amount: application?.lease?.lease_pricing?.deposit
      ? currency(application.lease.lease_pricing.deposit.cents, { fromCents: true })
      : null,
    key_deposit: application?.lease?.lease_pricing?.key_deposit
      ? currency(application.lease.lease_pricing.key_deposit.cents, { fromCents: true })
      : null,
    deposits: (application?.lease?.prepayments || []).map((prepayment) => ({
      amount: currency(prepayment.amount.cents, { fromCents: true }),
      date: moment(prepayment.created_at),
      paid_in_person: prepayment.paid_or_received,
    })),
    incentives: uniqBy(
      application?.lease?.rental_incentives || [],
      // one time incentives will have null incentive template IDs - include them all.
      (rentalIncentive) => rentalIncentive.incentive_template_id || Math.random()
    ).map((incentive) => {
      return {
        // For both benefit-based and one-time incentives:
        id: incentive.incentive_template_id || incentive.id,
        name: incentive.name,

        // For benefit-based incentives:
        benefit: incentive.benefit,
        internal_name: incentive.internal_name,
        internal_code: incentive.internal_code,

        // For one-time incentives (to allow the RentalIncentive object itself
        // to be updated correctly:
        effective_date: incentive.effective_date,
        description: incentive.description,
        benefit_id: incentive.benefit_id,
        duration_in_months: incentive.duration_in_months,
        amount: incentive.amount,
      };
    }),
    lease_items: [...lease_items],
    fee_templates: (application?.applied_fees || []).map(({ fee_template }) => ({
      id: fee_template.id,
      internal_name: fee_template.internal_name,
      fee_events: fee_template.fee_events.map((feeEvent) => ({
        id: feeEvent.id,
        action: feeEvent.action,
        amount: feeEvent.amount,
        calculation_type: feeEvent.calculation_type,
        amount_value: feeEvent.calculation_type === 'amount' ? feeEvent.amount : null,
        percent_value: feeEvent.calculation_type === 'percent' ? feeEvent.amount : null,
        created_at: feeEvent.created_at,
        updated_at: feeEvent.updated_at,
        yuhu_module: feeEvent.yuhu_module,
        trigger: { value: feeEvent.trigger, label: Triggers[feeEvent.trigger] },
      })),
    })),
    services: application?.lease?.services_and_utilities?.services,
    utilities: application?.lease?.services_and_utilities?.utilities,
  };
};

export const getSubtitle = (currentApplication) => {
  let subTitle = null;

  if (currentApplication.unit) {
    subTitle = `${currentApplication.property.name}, Unit ${currentApplication.unit.unit_number}`;
  } else if (currentApplication.unit_type) {
    subTitle = `${currentApplication.property.name}, ${currentApplication.unit_type.name}`;
  } else {
    subTitle = currentApplication.property.name;
  }

  return subTitle;
};

export const getDefaultTabSelected = (tabList, tabPath) => {
  if (tabPath) {
    const foundTabFromPath = tabList.find((tab) => tab.key === tabPath);

    if (foundTabFromPath) {
      return foundTabFromPath.key;
    }
  }

  return tabList[0].key;
};
